import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { HardDrives, Plus, Cpu, CheckCircle, Warning, Trash, Plugs, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATE_STYLE = {
  ready: "bg-success text-success-foreground", pending: "bg-accent text-accent-foreground",
  checking: "bg-accent text-accent-foreground", unreachable: "bg-destructive/10 text-destructive",
  unhealthy: "bg-warning text-warning-foreground", maintenance: "bg-warning text-warning-foreground",
  disabled: "bg-muted text-muted-foreground",
};

function NodeCard({ node, onAction, busy }) {
  const pf = node.preflight;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-border bg-card p-6" data-testid={`node-${node.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-display text-xl font-medium">{node.name}</h3>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATE_STYLE[node.state] || STATE_STYLE.pending}`} data-testid={`node-state-${node.id}`}>{node.state}</span>
          </div>
          <p className="mt-1 font-mono text-sm text-muted-foreground">{node.ssh_user}@{node.host}:{node.ssh_port}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{node.provider_label} · {node.region || "no region"} · {node.apps} app(s)</p>
          {node.last_connection && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-success-foreground" data-testid={`node-verified-${node.id}`}>
              <CheckCircle size={12} weight="fill" /> SSH verified {new Date(node.last_connection).toLocaleString()}
            </p>
          )}
        </div>
        <div className="text-right text-xs text-muted-foreground">
          {node.os && <p>{node.os}</p>}
          {node.capacity && <p>{node.capacity.cpu ?? "?"} CPU · {node.capacity.mem_total_mb ?? "?"} MB · {node.capacity.disk_avail_gb ?? "?"} GB free</p>}
        </div>
      </div>

      {pf && (
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {pf.checks.map((c, i) => (
            <span key={i} className="flex items-center gap-1.5 rounded-lg bg-secondary px-2.5 py-1.5 text-xs">
              {c.status === "PASS" ? <CheckCircle size={13} weight="fill" className="text-success-foreground" />
                : c.status === "WARNING" ? <Warning size={13} weight="fill" className="text-warning-foreground" />
                : <Warning size={13} weight="fill" className="text-destructive" />}
              {c.label}
            </span>
          ))}
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => onAction(node, "test")} data-testid={`node-test-${node.id}`}><Plugs size={14} /> Test</Button>
        <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => onAction(node, "preflight")} data-testid={`node-preflight-${node.id}`}><Cpu size={14} /> Preflight</Button>
        <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => onAction(node, "prepare")} data-testid={`node-prepare-${node.id}`}>Prepare</Button>
        <Button size="sm" className="rounded-full" disabled={busy || !pf?.ok} onClick={() => onAction(node, "activate")} data-testid={`node-activate-${node.id}`}><ShieldCheck size={14} /> Activate</Button>
        {node.state !== "maintenance"
          ? <Button size="sm" variant="ghost" className="rounded-full" disabled={busy} onClick={() => onAction(node, "maintenance")} data-testid={`node-maintenance-${node.id}`}>Maintenance</Button>
          : <Button size="sm" variant="ghost" className="rounded-full" disabled={busy} onClick={() => onAction(node, "ready")}>Resume</Button>}
        <AlertDialog>
          <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="rounded-full text-destructive hover:bg-destructive/10 hover:text-destructive" data-testid={`node-remove-${node.id}`}><Trash size={14} /> Remove</Button></AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Remove this node?</AlertDialogTitle>
              <AlertDialogDescription>This disconnects {node.name} from Hostless. Its stored SSH key is deleted. Apps must be moved off first.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => onAction(node, "remove")} className="rounded-full bg-destructive hover:bg-destructive/90" data-testid={`node-confirm-remove-${node.id}`}>Remove node</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </motion.div>
  );
}

export default function OwnerInfrastructure() {
  const [nodes, setNodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [exp, setExp] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", host: "", ssh_port: 22, ssh_user: "root", ssh_key: "", region: "" });

  const load = () => api.get("/owner/infra/nodes").then((r) => setNodes(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/owner/export-readiness").then((r) => setExp(r.data)).catch(() => {}); }, []);

  const addNode = async () => {
    try {
      await api.post("/owner/infra/nodes", { ...form, ssh_port: Number(form.ssh_port) });
      toast.success("Server added. Now test the connection.");
      setOpen(false);
      setForm({ name: "", host: "", ssh_port: 22, ssh_user: "root", ssh_key: "", region: "" });
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const onAction = async (node, act) => {
    setBusy(true);
    try {
      if (act === "test") { const { data } = await api.post(`/owner/infra/nodes/${node.id}/test`); data.ok ? toast.success(data.detail) : toast.error(data.detail); }
      else if (act === "preflight") { const { data } = await api.post(`/owner/infra/nodes/${node.id}/preflight`); toast[data.ok ? "success" : "info"](data.ok ? "Preflight passed" : "Preflight found issues"); }
      else if (act === "prepare") { const { data } = await api.post(`/owner/infra/nodes/${node.id}/prepare`); toast.info(data.message); }
      else if (act === "activate") { await api.post(`/owner/infra/nodes/${node.id}/activate`); toast.success("Node activated"); }
      else if (act === "maintenance") { await api.post(`/owner/infra/nodes/${node.id}/state`, { state: "maintenance" }); }
      else if (act === "ready") { await api.post(`/owner/infra/nodes/${node.id}/state`, { state: "ready" }); }
      else if (act === "remove") { await api.delete(`/owner/infra/nodes/${node.id}`); toast.success("Node removed"); }
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-6 py-10 sm:px-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="font-display text-4xl font-bold tracking-tight">Infrastructure</h1>
            <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold">Owner only</span>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-full active:scale-95" data-testid="add-node-button"><Plus size={18} weight="bold" /> Add server</Button></DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Connect a Linux server</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5"><Label>Display name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Launch VPS" data-testid="node-name" className="rounded-xl" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2 space-y-1.5"><Label>IP / hostname</Label><Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="203.0.113.10" data-testid="node-host" className="rounded-xl" /></div>
                  <div className="space-y-1.5"><Label>SSH port</Label><Input type="number" value={form.ssh_port} onChange={(e) => setForm({ ...form, ssh_port: e.target.value })} data-testid="node-port" className="rounded-xl" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>SSH user</Label><Input value={form.ssh_user} onChange={(e) => setForm({ ...form, ssh_user: e.target.value })} data-testid="node-user" className="rounded-xl" /></div>
                  <div className="space-y-1.5"><Label>Region (label)</Label><Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="eu-west" data-testid="node-region" className="rounded-xl" /></div>
                </div>
                <div className="space-y-1.5"><Label>SSH private key</Label>
                  <Textarea rows={5} value={form.ssh_key} onChange={(e) => setForm({ ...form, ssh_key: e.target.value })} placeholder="-----BEGIN OPENSSH PRIVATE KEY-----" data-testid="node-key" className="rounded-xl font-mono text-xs" />
                  <p className="text-xs text-muted-foreground">Encrypted at rest. Never shown again after saving.</p>
                </div>
              </div>
              <DialogFooter><Button onClick={addNode} className="rounded-full" data-testid="save-node">Add server</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <p className="mt-2 text-muted-foreground">Connect a cloud VPS or your own hardware. Cloud and physical servers are the same to Hostless.</p>

        <Tabs defaultValue="nodes" className="mt-8">
          <TabsList className="rounded-full">
            <TabsTrigger value="nodes" className="rounded-full" data-testid="infra-tab-nodes">Nodes</TabsTrigger>
            <TabsTrigger value="export" className="rounded-full" data-testid="infra-tab-export">Export readiness</TabsTrigger>
          </TabsList>

          <TabsContent value="nodes" className="mt-6 space-y-4">
            {nodes.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-border bg-card p-16 text-center">
                <HardDrives size={36} weight="duotone" className="mx-auto text-muted-foreground" />
                <p className="mt-4 text-muted-foreground">No servers connected yet. Add a Linux VPS to start hosting real apps.</p>
              </div>
            ) : nodes.map((n) => <NodeCard key={n.id} node={n} onAction={onAction} busy={busy} />)}
          </TabsContent>

          <TabsContent value="export" className="mt-6">
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="export-readiness">
              <div className="flex items-center justify-between">
                <p className="font-display text-xl font-medium">Self-host export readiness</p>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${exp?.ready ? "bg-success text-success-foreground" : "bg-warning text-warning-foreground"}`}>
                  {exp?.ready ? "Ready to self-host" : "Not ready"}
                </span>
              </div>
              <ul className="mt-5 space-y-2">
                {(exp?.checks || []).map((c, i) => (
                  <li key={i} className="flex items-center justify-between rounded-xl bg-secondary px-4 py-2.5 text-sm">
                    <span className="flex items-center gap-2">{c.ok ? <CheckCircle size={16} weight="fill" className="text-success-foreground" /> : <Warning size={16} weight="fill" className="text-warning-foreground" />}{c.label}</span>
                    <span className="text-xs text-muted-foreground">{c.detail}</span>
                  </li>
                ))}
              </ul>
              {(exp?.notes || []).length > 0 && (
                <div className="mt-4 space-y-2">
                  {exp.notes.map((n, i) => <p key={i} className="rounded-xl bg-warning/30 px-3 py-2 text-xs text-warning-foreground">{n}</p>)}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
